<template>
  <BaseModal
    :model-value="modelValue"
    title="Manage Prompts"
    width="1100px"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('update:modelValue', false)"
  >
    <template #title-icon>
    <img src="@/assets/encounterNotes/manage.svg" alt="Manage Prompts" class="w-6 h-6">
    </template>

    <!-- Delete Confirmation Modal -->
    <BaseModal
      v-model="showDeleteConfirm"
      title="Delete Template"
      width="640px"
      :show-close="true"
      description="Are you sure you want to delete this template? This action cannot be undone."
      dialog-type="error"
      @close="showDeleteConfirm = false"
    >
      <template #footer>
        <div class="delete-modal-actions">
          <ResponsiveButton button_type="outlined" built_in_theme="error" @click-button="showDeleteConfirm = false">
            Cancel
          </ResponsiveButton>
          <ResponsiveButton built_in_theme="error" @click-button="confirmDeleteTemplate">
            Yes, delete template.
          </ResponsiveButton>
        </div>
      </template>
    </BaseModal>

    <div class="manage-prompts-body">
      <!-- Left Sidebar -->
      <div class="sidebar">
        <!-- Create New Template button -->
        <div class="sidebar-top">
          <button
            class="create-new-btn"
            :class="{ 'create-new-btn--active': isCreatingNew }"
            @click="handleCreateNew"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
            </svg>
            Create New Template
          </button>
        </div>

        <!-- Prompt List -->
        <div v-if="prompts.length > 0" class="prompt-list">
          <p class="prompt-list-heading">Prompt List</p>
          <div v-if="isLoading" class="sidebar-loading">Loading...</div>
          <template v-else>
            <button
              v-for="prompt in prompts"
              :key="prompt.id"
              button_type="text"
              width_type="fill-whole"
              class="prompt-item"
              :class="{ 'prompt-item--active': !isCreatingNew && selectedPromptId === prompt.id }"
              @click="handleSelectPrompt(prompt.id)"
            >
              {{ prompt.name }}{{ prompt.id === currentPromptId ? ' (Current)' : '' }}
            </button>
          </template>
        </div>
      </div>

      <!-- Right Content Panel -->
      <div class="content-panel">
        <!-- Loading state -->
        <div v-if="isLoading && !isCreatingNew" class="content-loading">
          Loading prompts...
        </div>

        <!-- Create New / Edit Template Form Mode -->
        <template v-else-if="isCreatingNew || isEditing">
          <div class="content-actions">
            <ResponsiveButton
              v-component-loading="isSaving"
              :disabled="!canSave || isSaving"
              @click-button="handleSaveTemplate"
            >
              Save Template
            </ResponsiveButton>
          </div>

          <div class="template-form">
            <div class="field-row">
              <div class="field-label">
                <div>Template Name</div>
                <div class="field-required">(Required)</div>
              </div>
              <div class="field-input-row">
                <TextInput
                  v-model="newTemplateName"
                  class="flex-1"
                  placeholder="Enter template name"
                  :clearable="false"
                />
                <ResponsiveButton
                  v-if="newTemplateName"
                  button_type="text"
                  class="field-clear-btn"
                  @click-button="newTemplateName = ''"
                >
                  Clear
                </ResponsiveButton>
              </div>
            </div>

            <TextArea
              v-model="newTemplateContent"
              class="template-textarea"
              placeholder="Enter prompt instructions..."
              :clearable="false"
            />
          </div>
        </template>

        <!-- View Existing Prompt Mode -->
        <template v-else-if="selectedPrompt">
          <div class="content-actions">
            <template v-if="!selectedPrompt.is_system">
              <ResponsiveButton button_type="outlined" built_in_theme="error" @click-button="showDeleteConfirm = true">
                Delete Template
              </ResponsiveButton>
              <ResponsiveButton button_type="outlined" @click-button="handleEditTemplate">
                Edit Template
              </ResponsiveButton>
            </template>
            <ResponsiveButton @click-button="handleApplyAndRegenerate">
              Apply &amp; Regenerate
            </ResponsiveButton>
          </div>

          <div class="template-form">
            <div class="field-row">
              <div class="field-label">
                <span>Template Name</span>
                <span class="field-required">(Required)</span>
              </div>
              <TextInput
                class="flex-1"
                :model-value="selectedPrompt.name"
                disabled
                :clearable="false"
              />
            </div>

            <div class="template-content-view">
              {{ selectedPrompt.content }}
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <template v-else>
          <div class="content-empty">
            Select a prompt from the list or create a new template.
          </div>
        </template>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import { getPromptsApi, createPromptApi, updatePromptApi, deletePromptApi } from '@/api/recordingApi';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  currentPromptId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'apply']);

const prompts = ref([]);
const selectedPromptId = ref(null);
const isCreatingNew = ref(false);
const isEditing = ref(false);
const newTemplateName = ref('');
const newTemplateContent = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const showDeleteConfirm = ref(false);

const selectedPrompt = computed(() =>
  prompts.value.find((p) => p.id === selectedPromptId.value) || null
);

const canSave = computed(() => newTemplateName.value.trim().length > 0);

const fetchPrompts = async () => {
  isLoading.value = true;
  try {
    const data = await getPromptsApi();
    prompts.value = data.prompts || [];
    // Auto-select the current prompt if set
    if (props.currentPromptId && !selectedPromptId.value) {
      const match = prompts.value.find((p) => p.id == props.currentPromptId);
      if (match) {
        selectedPromptId.value = match.id;
        isCreatingNew.value = false;
      }
    }
  } catch (error) {
    console.error('Failed to fetch prompts:', error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      selectedPromptId.value = null;
      isCreatingNew.value = false;
      isEditing.value = false;
      newTemplateName.value = '';
      newTemplateContent.value = '';
      fetchPrompts();
    }
  }
);

const handleCreateNew = () => {
  isCreatingNew.value = true;
  isEditing.value = false;
  selectedPromptId.value = null;
  newTemplateName.value = '';
  newTemplateContent.value = '';
};

const handleEditTemplate = () => {
  if (!selectedPrompt.value) return;
  newTemplateName.value = selectedPrompt.value.name;
  newTemplateContent.value = selectedPrompt.value.content;
  isEditing.value = true;
  isCreatingNew.value = false;
};

const handleSelectPrompt = (id) => {
  selectedPromptId.value = id;
  isCreatingNew.value = false;
  isEditing.value = false;
};

const handleApplyAndRegenerate = () => {
  emit('apply', selectedPromptId.value);
  emit('update:modelValue', false);
};

const handleSaveTemplate = async () => {
  if (!canSave.value) return;
  isSaving.value = true;
  try {
    if (isEditing.value && selectedPromptId.value) {
      await updatePromptApi(selectedPromptId.value, {
        name: newTemplateName.value.trim(),
        content: newTemplateContent.value,
      });
      await fetchPrompts();
      isEditing.value = false;
    } else {
      const created = await createPromptApi({
        name: newTemplateName.value.trim(),
        content: newTemplateContent.value,
      });
      await fetchPrompts();
      selectedPromptId.value = created.id;
      isCreatingNew.value = false;
    }
    newTemplateName.value = '';
    newTemplateContent.value = '';
  } catch (error) {
    console.error('Failed to save template:', error);
  } finally {
    isSaving.value = false;
  }
};

const confirmDeleteTemplate = async () => {
  if (!selectedPromptId.value) return;
  try {
    await deletePromptApi(selectedPromptId.value);
    showDeleteConfirm.value = false;
    selectedPromptId.value = null;
    await fetchPrompts();
  } catch (error) {
    console.error('Failed to delete template:', error);
  }
};
</script>

<style scoped>
.manage-prompts-body {
  display: flex;
  height: 600px;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  display: flex;
  flex-direction: column;
  gap: 19px;
  width: 272px;
  flex-shrink: 0;
  padding: 20px 21px;
  overflow-y: auto;
}

.sidebar-top {
  display: flex;
  flex-direction: column;
}

.create-new-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #004879;
  text-align: left;
  width: 100%;
  transition: background-color 0.15s ease;
}

.create-new-btn:hover {
  background: rgba(0, 72, 121, 0.08);
}

.create-new-btn--active {
  background: rgba(0, 72, 121, 0.08);
}

.prompt-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.prompt-list-heading {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333333;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-loading {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666;
  padding: 8px 12px;
}

.prompt-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333333;
  text-align: left;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.15s ease;
}

.prompt-item:hover {
  background: rgba(0, 72, 121, 0.05);
}

.prompt-item--active {
  background: rgba(0, 72, 121, 0.08);
  color: #004879;
}

/* Right content panel */
.content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: rgba(85, 85, 85, 0.06);
  padding: 20px;
  overflow-y: auto;
  min-width: 0;
}

.content-loading,
.content-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666666;
}

/* Action buttons row */
.content-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

/* Template form */
.template-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.field-row {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-shrink: 0;
}

.field-label {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #4a4a4a;
  line-height: normal;
}

.field-required {
  color: #D10000;
  font-size: 14px;
}

.field-input-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.field-clear-btn {
  flex-shrink: 0;
}

/* Textarea wrapper layout for new template (TextArea handles its own border/padding).
 * Library TextArea sizes its inner <textarea> via the `rows` prop, so to match the
 * original 400px / flex-fill behavior we pierce scoped style and set min-height
 * directly on the inner textarea element. */
.template-textarea {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.template-textarea :deep(textarea) {
  min-height: 400px;
  flex: 1;
}

/* Delete confirmation modal */
.delete-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

/* Content view for existing prompt */
.template-content-view {
  flex: 1;
  padding: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #333333;
  white-space: pre-wrap;
  overflow-y: auto;
  background: transparent;
  border-radius: 4px;
  min-height: 400px;
}
</style>
